import React, { useCallback, useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/admin/ResourceTable";
import { LifeBuoy, ShieldCheck, Check, X } from "lucide-react";

const dt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");
const TH = ({ cols }) => <thead><tr className="bg-gray-50 border-b">{cols.map((h) => <th key={h} className="py-3 px-4 text-[11px] uppercase tracking-wider text-gray-600 font-bold whitespace-nowrap">{h}</th>)}</tr></thead>;

const Tickets = ({ status }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/support-tickets", { params: { status, page_size: 100 } }).then((r) => setItems(r.data.items)).catch((e) => toast.error(apiErr(e))), [status]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (t) => {
    const note = window.prompt(`Resolution note for query #${t.number} (optional)`);
    if (note === null) return;
    try { await api.patch(`/support-tickets/${t.id}/resolve`, { note }); toast.success(`Query #${t.number} resolved`); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <table className="w-full text-sm text-left" data-testid="support-tickets-table">
      <TH cols={["#", "Member", "Mobile", "Query", "Received", "Status", ""]} />
      <tbody>
        {items.map((t) => (
          <tr key={t.id} className="border-b border-gray-100 align-top" data-testid={`support-ticket-${t.id}`}>
            <td className="py-3 px-4 font-mono text-xs">#{t.number}</td>
            <td className="py-3 px-4 font-semibold text-gray-900 whitespace-nowrap">{t.name || "—"}</td>
            <td className="py-3 px-4 font-mono text-xs">{t.mobile}</td>
            <td className="py-3 px-4 text-gray-700 max-w-md whitespace-pre-line">{t.message}{t.resolution_note ? <div className="text-xs text-green-700 mt-1">Note: {t.resolution_note}</div> : null}</td>
            <td className="py-3 px-4 text-xs text-gray-500 whitespace-nowrap">{dt(t.created_at)}</td>
            <td className="py-3 px-4"><Badge tone={t.status === "open" ? "amber" : "green"}>{t.status}</Badge></td>
            <td className="py-3 px-4">{t.status === "open" && <button data-testid={`support-resolve-${t.id}`} onClick={() => resolve(t)} className="text-xs font-bold text-white bg-[#8B0000] hover:bg-[#6B0000] px-3 py-1.5 rounded-lg">Mark resolved</button>}</td>
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-gray-400" data-testid="support-empty">No queries</td></tr>}
      </tbody>
    </table>
  );
};

const Kyc = ({ status }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/kyc-requests", { params: { status, page_size: 100 } }).then((r) => setItems(r.data.items)).catch((e) => toast.error(apiErr(e))), [status]);
  useEffect(() => { load(); }, [load]);
  const decide = async (k, decision) => {
    try { await api.patch(`/kyc-requests/${k.id}`, { decision }); toast.success(decision === "verified" ? "PAN approved — member notified on WhatsApp" : "PAN rejected — member notified"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <table className="w-full text-sm text-left" data-testid="kyc-table">
      <TH cols={["Member", "Mobile", "PAN", "Submitted", "Status", ""]} />
      <tbody>
        {items.map((k) => (
          <tr key={k.id} className="border-b border-gray-100" data-testid={`kyc-row-${k.id}`}>
            <td className="py-3 px-4 font-semibold text-gray-900">{k.name || "—"}</td>
            <td className="py-3 px-4 font-mono text-xs">{k.mobile}</td>
            <td className="py-3 px-4 font-mono text-xs">{k.pan_masked}</td>
            <td className="py-3 px-4 text-xs text-gray-500 whitespace-nowrap">{dt(k.updated_at)}</td>
            <td className="py-3 px-4"><Badge tone={{ verified: "green", rejected: "red" }[k.status] || "amber"}>{k.status}</Badge></td>
            <td className="py-3 px-4 whitespace-nowrap">{k.status === "pending_review" && (
              <div className="flex gap-2">
                <button data-testid={`kyc-approve-${k.id}`} onClick={() => decide(k, "verified")} className="flex items-center gap-1 text-xs font-bold text-white bg-green-700 hover:bg-green-800 px-3 py-1.5 rounded-lg"><Check className="h-3.5 w-3.5" /> Approve</button>
                <button data-testid={`kyc-reject-${k.id}`} onClick={() => decide(k, "rejected")} className="flex items-center gap-1 text-xs font-bold text-red-700 border border-red-200 hover:bg-red-50 px-3 py-1.5 rounded-lg"><X className="h-3.5 w-3.5" /> Reject</button>
              </div>)}</td>
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-gray-400" data-testid="kyc-empty">No KYC requests</td></tr>}
      </tbody>
    </table>
  );
};

export default function MemberRequests() {
  const [tab, setTab] = useState("support");
  const [status, setStatus] = useState("");
  const filters = tab === "support" ? [["", "All"], ["open", "Open"], ["resolved", "Resolved"]] : [["", "All"], ["pending_review", "Pending"], ["verified", "Verified"], ["rejected", "Rejected"]];
  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Member Requests</h1>
      <p className="text-sm text-gray-500 mt-1 mb-4">Support queries and PAN verifications received from the WhatsApp bot</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {[["support", "Support queries", LifeBuoy], ["kyc", "KYC / PAN", ShieldCheck]].map(([k, l, I]) => (
          <button key={k} data-testid={`requests-tab-${k}`} onClick={() => { setTab(k); setStatus(""); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${tab === k ? "bg-[#8B0000] text-white" : "bg-white border border-gray-200 text-gray-600"}`}><I className="h-4 w-4" /> {l}</button>
        ))}
        <div className="ml-auto flex gap-1">
          {filters.map(([k, l]) => (
            <button key={k || "all"} data-testid={`requests-filter-${k || "all"}`} onClick={() => setStatus(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${status === k ? "bg-gray-900 text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        {tab === "support" ? <Tickets status={status} /> : <Kyc status={status} />}
      </div>
    </div>
  );
}
